import { ImgID } from "../../../image_loader/img_id.js";
import { Vector2 } from "../../../common/vector2.js";
import { Easing } from "../../../transforms/transform_base.js";
import { TransformOpacity } from "../../../transforms/transform_opacity.js";
import { TransformRotate } from "../../../transforms/transform_rotate.js";
import { TransformScale } from "../../../transforms/transform_scale.js";
import { TransformCircularMove } from "../../../transforms/transform_circular_move.js";
import { TransformStraightMove } from "../../../transforms/transform_straight_move.js";
import { Transformable } from "../../../transforms/transformable.js";
import { ColorScheme } from "../../common_ui_elements/color_scheme.js";
import { TextStyleScheme } from "../../common_ui_elements/text_style_scheme.js";
import { UIElementHolder } from "../../common_ui_elements/ui_element_holder.js";
import { UIImage } from "../../common_ui_elements/ui_image.js";
import { MyTextAlign, MyTextVerticalAlign, UITextElement } from "../../common_ui_elements/ui_text_element.js";
import { AbstractOverlay } from "../abstract_overlay.js";
import { OverlayId } from "../overlay_ids.js";
import { OverlayManager } from "../overlay_manager.js";

/**
 * First-time explanation for the daily tasks screen.
 *
 * The composition uses a fixed design-space holder so the relationship between
 * the callouts remains intact on tall and wide phones. The whole holder is then
 * centered by the same LayoutScaler used throughout the game UI.
 */
export class DailyTasksOverlay extends AbstractOverlay
{
  private static readonly DESIGN_W = 982;
  private static readonly DESIGN_H = 1650;
  private static readonly STAGGER_MS = 56;
  private static readonly BASE_DELAY_MS = 160;
  private static readonly ITEM_ANIM_DURATION_MS = 256;
  private static readonly ARROW_ANIM_DURATION_MS = 280;
  private static readonly TITLE_ANIM_DURATION_MS = 320;

  private readonly content_holder: UIElementHolder;

  public constructor(
    ctx: CanvasRenderingContext2D,
    x: number,
    y: number,
    width: number,
    height: number,
    params: unknown,
    overlay_id: OverlayId
  )
  {
    super( ctx, x, y, width, height, overlay_id, params, false );

    const holder_w = LayoutScaler.widthFromDesign( DailyTasksOverlay.DESIGN_W );
    const holder_h = LayoutScaler.heightFromDesign( DailyTasksOverlay.DESIGN_H );
    this.content_holder = new UIElementHolder(
      this.ctx,
      this.rect.getW2() - holder_w / 2,
      this.rect.getH2() - holder_h / 2,
      holder_w,
      holder_h
    );
    this.addChild( this.content_holder );

    this.createElements();
  }

  private createElements(): void
  {
    const base_now = performance.now() + DailyTasksOverlay.BASE_DELAY_MS;
    const stagger = DailyTasksOverlay.STAGGER_MS;

    const addAppearTransform = ( element: Transformable, start_time: number ): void =>
    {
      element.setOpacity( 0 );
      element.setScale( new Vector2( 0.6, 0.6 ) );
      element.addTransform( new TransformOpacity( element, true, 0, 1, start_time, DailyTasksOverlay.ITEM_ANIM_DURATION_MS, { easing: Easing.EaseOutQuad } ) );
      element.addTransform( new TransformScale( element, true, null, new Vector2( 1, 1 ), start_time, DailyTasksOverlay.ITEM_ANIM_DURATION_MS, { easing: Easing.EaseOutQuad } ) );
    };

    this.createArrowTop( base_now + stagger * 4 );
    this.createArrowBottom( base_now + stagger );
    this.createTopCallout( base_now, stagger, addAppearTransform );
    this.createCenterCallout( base_now, stagger, addAppearTransform );
    this.createBottomCallout( base_now, stagger, addAppearTransform );
    this.createTitle();
    this.createCloseButton( base_now + stagger * 9 );
  }

  private static arcParams(
    from: Vector2,
    to: Vector2,
    bulge: number
  ): { center: Vector2; radius: number; start_angle: number; end_angle: number }
  {
    const mid = new Vector2( ( from.x + to.x ) / 2, ( from.y + to.y ) / 2 );
    const dx = to.x - from.x;
    const dy = to.y - from.y;
    const len = Math.sqrt( dx * dx + dy * dy );
    const perp_x = -dy / len;
    const perp_y = dx / len;
    const center = new Vector2( mid.x + perp_x * bulge, mid.y + perp_y * bulge );
    const radius = Math.sqrt( ( from.x - center.x ) ** 2 + ( from.y - center.y ) ** 2 );
    const start_angle = Math.atan2( from.y - center.y, from.x - center.x );
    let diff = Math.atan2( to.y - center.y, to.x - center.x ) - start_angle;
    while ( diff > Math.PI ) diff -= 2 * Math.PI;
    while ( diff < -Math.PI ) diff += 2 * Math.PI;
    const end_angle = start_angle + diff;
    return { center, radius, start_angle, end_angle };
  }

  private createTitle(): void
  {
    // The Figma title frame spans the complete screen width (the visible text
    // itself remains centered inside it), rather than being constrained by the
    // tutorial callouts holder.
    const title_x = 0;
    const title_y = LayoutScaler.yFromDesign( 94 );
    const title_offset_y = LayoutScaler.yFromDesign( 20 );
    const now = performance.now();
    const title = new UITextElement(
      this.ctx,
      title_x,
      title_y,
      this.rect.getW(),
      LayoutScaler.heightFromDesign( 128 ),
      {
        localization_key: "DAILY_TASKS.TITLE",
        font_size: LayoutScaler.textSizeFromDesign( 60 ),
        min_font_size: LayoutScaler.textSizeFromDesign( 42 ),
        font_weight: 900,
        text_color: ColorScheme.white,
        gradient_text_color: {
          direction: "vertical",
          stops: [
            { offset: 0, color: "#FFFFFF" },
            { offset: 1, color: "#FFDE8D" },
          ],
        },
        text_style: TextStyleScheme.geologica,
        text_align: MyTextAlign.Center,
        vertical_align: MyTextVerticalAlign.Center,
        outline_width: LayoutScaler.textBorderSizeFromDesign( 8 ),
        outline_color: "#05378B",
        shadow_blur: LayoutScaler.widthFromDesign( 2 ),
        shadow_color: "#C75C00",
        shadow_offset_x: 0,
        shadow_offset_y: LayoutScaler.yFromDesign( 5 ),
        auto_wrap: false,
        fit_font_size: true,
      }
    );

    title.setOpacity( 0 );
    title.setPixelPos( new Vector2( title_x, title_y + title_offset_y ), true );
    title.addTransform( new TransformOpacity( title, true, 0, 1, now, DailyTasksOverlay.TITLE_ANIM_DURATION_MS, { easing: Easing.EaseOutQuad } ) );
    title.addTransform( new TransformStraightMove( title, true, null, new Vector2( title_x, title_y ), now, DailyTasksOverlay.TITLE_ANIM_DURATION_MS, { easing: Easing.EaseOutQuad } ) );
    this.addChild( title );
  }

  private createTopCallout(
    base_now: number,
    stagger: number,
    addAppearTransform: ( element: Transformable, start_time: number ) => void
  ): void
  {
    const text = this.addText(
      "DAILY_TASKS_OVERLAY.TOP",
      60,
      300,
      493,
      108,
      MyTextAlign.Left,
      { font_size: 50, font_weight: 900, line_height: 1.17, auto_wrap: true }
    );
    const cannon = this.addImage( ImgID.UI_DailyTasksTop, 602, 170, 360, 360 );
    cannon.setFlipX( true );

    addAppearTransform( cannon, base_now );
    addAppearTransform( text, base_now + stagger );
  }

  private createCenterCallout(
    base_now: number,
    stagger: number,
    addAppearTransform: ( element: Transformable, start_time: number ) => void
  ): void
  {
    const tasks = this.addImage( ImgID.UI_DailyTasksCenter, -6, 567, 416, 332 );
    const text = this.addText(
      "DAILY_TASKS_OVERLAY.CENTER",
      459,
      676,
      440,
      120,
      MyTextAlign.Center,
      { font_size: 50, font_weight: 900, line_height: 1.17, auto_wrap: true }
    );


    addAppearTransform( tasks, base_now + stagger * 3 );
    addAppearTransform( text, base_now + stagger * 4 );
  }

  private createBottomCallout(
    base_now: number,
    stagger: number,
    addAppearTransform: ( element: Transformable, start_time: number ) => void
  ): void
  {
    const rewards = this.addImage( ImgID.UI_DailyTasksBottom, 155, 1025, 671, 345 );
    const text = this.addText(
      "DAILY_TASKS_OVERLAY.BOTTOM",
      105,
      1368,
      772,
      120,
      MyTextAlign.Center,
      { font_size: 38, font_weight: 900, line_height: 1.1, auto_wrap: true }
    );

    addAppearTransform( rewards, base_now + stagger * 6 );
    addAppearTransform( text, base_now + stagger * 7 );
  }

  private createArrowTop( start_time: number ): void
  {
    const arrow_x = LayoutScaler.xFromDesign( 207 );
    const arrow_y = LayoutScaler.yFromDesign( 946 );
    const arrow_w = LayoutScaler.widthFromDesign( 168 );
    const arrow_h = LayoutScaler.heightFromDesign( 155 );
    const arrow_start_x = LayoutScaler.xFromDesign( 202 );
    const arrow_start_y = LayoutScaler.yFromDesign( 733 );
    const rotation_from = -35 * Math.PI / 180;
    const rotation_to = 0;

    const arrow = new UIImage(
      this.ctx,
      arrow_start_x,
      arrow_start_y,
      arrow_w,
      arrow_h,
      ImgID.UI_DailyTasksArrow
    );
    arrow.setOpacity( 0 );
    arrow.setRotation( rotation_from );

    const arc = DailyTasksOverlay.arcParams(
      new Vector2( arrow_start_x, arrow_start_y ),
      new Vector2( arrow_x, arrow_y ),
      LayoutScaler.widthFromDesign( -120 )
    );

    arrow.addTransform( new TransformOpacity( arrow, true, 0, 1, start_time, DailyTasksOverlay.ARROW_ANIM_DURATION_MS, { easing: Easing.EaseOutQuad } ) );
    arrow.addTransform( new TransformRotate( arrow, true, rotation_from, rotation_to, start_time, DailyTasksOverlay.ARROW_ANIM_DURATION_MS, { easing: Easing.EaseOutQuad } ) );
    arrow.addTransform( new TransformCircularMove( arrow, true, arc.center, arc.radius, start_time, DailyTasksOverlay.ARROW_ANIM_DURATION_MS, arc.start_angle, arc.end_angle, { easing: Easing.EaseOutQuad } ) );

    this.content_holder.addChild( arrow );
  }

  private createArrowBottom( start_time: number ): void
  {
    const arrow_x = LayoutScaler.xFromDesign( 551 );
    const arrow_y = LayoutScaler.yFromDesign( 500 );
    const arrow_w = LayoutScaler.widthFromDesign( 168 );
    const arrow_h = LayoutScaler.heightFromDesign( 155 );
    const arrow_start_x = LayoutScaler.xFromDesign( 782 );
    const arrow_start_y = LayoutScaler.yFromDesign( 370 );
    const rotation_from = 25 * Math.PI / 180;
    const rotation_to = 0;

    const arrow = new UIImage(
      this.ctx,
      arrow_start_x,
      arrow_start_y,
      arrow_w,
      arrow_h,
      ImgID.UI_DailyTasksArrow,
      { direction_x: -1, direction_y: 1 }
    );
    arrow.setOpacity( 0 );
    arrow.setRotation( rotation_from );

    const arc = DailyTasksOverlay.arcParams(
      new Vector2( arrow_start_x, arrow_start_y ),
      new Vector2( arrow_x, arrow_y ),
      LayoutScaler.widthFromDesign( 120 )
    );

    arrow.addTransform( new TransformOpacity( arrow, true, 0, 1, start_time, DailyTasksOverlay.ARROW_ANIM_DURATION_MS, { easing: Easing.EaseOutQuad } ) );
    arrow.addTransform( new TransformRotate( arrow, true, rotation_from, rotation_to, start_time, DailyTasksOverlay.ARROW_ANIM_DURATION_MS, { easing: Easing.EaseOutQuad } ) );
    arrow.addTransform( new TransformCircularMove( arrow, true, arc.center, arc.radius, start_time, DailyTasksOverlay.ARROW_ANIM_DURATION_MS, arc.start_angle, arc.end_angle, { easing: Easing.EaseOutQuad } ) );

    this.content_holder.addChild( arrow );
  }

  private createCloseButton( start_time: number ): void
  {
    const size = LayoutScaler.widthFromDesign( 110 );
    const bottom_margin = LayoutScaler.yFromDesign( 95 );
    const close = new UIImage(
      this.ctx,
      this.rect.getW2() - size / 2,
      this.rect.getH() - bottom_margin - size,
      size,
      size,
      ImgID.UI_DailyTasksCloseBtn,
      undefined,
      true
    );
    close.setOpacity( 0 );
    close.addTransform( new TransformOpacity( close, true, 0, 1, start_time, DailyTasksOverlay.ITEM_ANIM_DURATION_MS, { easing: Easing.EaseOutQuad } ) );
    close.setOnClick( () => OverlayManager.closeOverlay() );
    this.addChild( close );
  }

  private addText(
    localization_key: string,
    x: number,
    y: number,
    width: number,
    height: number,
    align: MyTextAlign,
    options: {
      font_size?: number;
      font_weight?: number;
      line_height?: number;
      auto_wrap?: boolean;
    } = {}
  ): UITextElement
  {
    const text = new UITextElement(
      this.ctx,
      LayoutScaler.xFromDesign( x ),
      LayoutScaler.yFromDesign( y ),
      LayoutScaler.widthFromDesign( width ),
      LayoutScaler.heightFromDesign( height ),
      {
        localization_key,
        font_size: LayoutScaler.textSizeFromDesign( options.font_size ?? 38 ),
        min_font_size: LayoutScaler.textSizeFromDesign( 30 ),
        font_weight: options.font_weight ?? 900,
        text_color: ColorScheme.white,
        text_style: TextStyleScheme.geologica,
        text_align: align,
        vertical_align: MyTextVerticalAlign.Center,
        auto_wrap: options.auto_wrap ?? true,
        fit_font_size: true,
        line_height: options.line_height ?? 1.1,
        ...TextStyleScheme.buttonShadow(),
      }
    );
    this.content_holder.addChild( text );
    return text;
  }

  private addImage(
    image_id: ImgID,
    x: number,
    y: number,
    width: number,
    height: number,
    options?: { direction_x?: number; direction_y?: number }
  ): UIImage
  {
    const image = new UIImage(
      this.ctx,
      LayoutScaler.xFromDesign( x ),
      LayoutScaler.yFromDesign( y ),
      LayoutScaler.widthFromDesign( width ),
      LayoutScaler.heightFromDesign( height ),
      image_id,
      options
    );
    this.content_holder.addChild( image );
    return image;
  }

  public render(): void
  {
    if ( !this.shouldBeRendered() ) return;
    this.startRender();
    this.finishRender();
  }

  public override onTouchStart( p: Vector2, now: number ): boolean
  {
    super.onTouchStart( p, now );
    OverlayManager.closeOverlay();
    return true;
  }

  public name(): string
  {
    return "DailyTasksOverlay";
  }
}
